/**
 * tab-system plugin
 *
 * Owns sections with `data-toggled-swiper="true"` end-to-end. The
 * initializer's `findGate` finds this plugin via `gateInit`, hands the
 * section over to `runGate`, and skips the normal per-swiper pipeline.
 *
 * Section markup (e.g. `featured-collections.liquid`):
 *
 *   .swiper-section[data-toggled-swiper="true"]
 *   ├── .carousel-tabs[role="tablist"]
 *   │   └── .carousel-tab[data-target="<panel-id>"][data-tab-collection-url=...]
 *   ├── .carousel-wrapper#<panel-id>(.is-hidden)
 *   │   ├── .swiper#<panel-id-suffixed-or-same>
 *   │   │   └── .swiper-wrapper > .swiper-slide × N      ← inline only on tab 1
 *   │   └── <template data-tab-products-template>        ← present on tabs 2-N
 *   │       └── .swiper-slide × N                          (cloned on first activation)
 *   └── ... one wrapper per tab; only the first is visible at init ...
 *
 * Behavior:
 *   - Keyboard nav: ArrowLeft/Right move focus across `.carousel-tab`
 *     buttons, auto-scrolling them into view. Wired ONCE at section init.
 *     (The legacy code re-wired on every activation, stacking listeners —
 *     fixed in this port.)
 *   - Click / Enter / Space activates a tab.
 *   - Activation toggles `is-active` / `aria-selected` on tabs and shows the
 *     matching `.carousel-wrapper` while hiding the others.
 *   - On first activation of each tab, slides are hydrated from the panel's
 *     `<template data-tab-products-template>` (if present) before Swiper init.
 *     This lets sections render only the first tab's slides inline at page
 *     load, with hidden tabs' content held inert in templates — browsers
 *     skip style recalc / image fetch / custom-element upgrade for inert
 *     template content. After hydration the template is removed.
 *   - The first activation of each tab lazy-creates a Swiper for the tab's
 *     panel via `helpers.initSwiper`, which runs the full plugin pipeline
 *     (parseOptions / afterInit / afterAllInit / onDestroy). Subsequent
 *     activations of the same tab call `.update()` so Swiper recomputes
 *     dimensions after the wrapper becomes visible.
 *   - View-all links (`.featured-collections__view-all`) get their `href`
 *     swapped to the active tab's `data-tab-collection-url` (preserved
 *     from legacy behavior).
 *
 * Coordination with size-preference-modal:
 * The size-preference modal refetches each tab's collection filtered by
 * the user's sizes and replaces `.swiper-wrapper.innerHTML` directly. For
 * hidden tabs that haven't been activated yet, this populates the wrapper
 * before our hydrate path would. `hydrateTabFromTemplate` therefore checks
 * "is the wrapper already populated?" and only clones from template if it
 * isn't — and unconditionally removes the template afterward so it doesn't
 * leak unused inert content.
 *
 * Resize: tabbed sections typically don't carry `data-mobile-breakpoint`,
 * so the initializer leaves the section registered across resizes. Swiper handles
 * its own resize observation per-instance. The section's tab listeners are
 * therefore wired exactly once for the lifetime of the page.
 */

export const tabSystem = {
  name: 'tab-system',

  gateInit(section) {
    return section?.dataset?.toggledSwiper === 'true';
  },

  runGate(section, _ctx, helpers) {
    setupKeyboardNav(section);

    /** Map<panelId, Swiper> — populated on first activation of each tab. */
    const tabSwipers = new Map();

    section.querySelectorAll('.carousel-tab').forEach((btn) => {
      const handler = () => activateTab(btn, section, tabSwipers, helpers);
      btn.addEventListener('click', handler);
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handler();
        }
      });
    });

    // Activate the tab marked `is-active` in markup, falling back to the
    // first tab. Calling the click handler directly (rather than dispatching
    // a click) keeps the path synchronous and avoids accidental focus moves.
    const initialTab =
      section.querySelector('.carousel-tab.is-active') ||
      section.querySelector('.carousel-tab');
    if (initialTab) activateTab(initialTab, section, tabSwipers, helpers);
  },
};

export default tabSystem;

// ---------- helpers ----------

function setupKeyboardNav(section) {
  section.querySelectorAll('.carousel-tabs').forEach((tablist) => {
    const tabs = Array.from(tablist.querySelectorAll('.carousel-tab'));

    tabs.forEach((tab, idx) => {
      tab.addEventListener('keydown', (e) => {
        let newIdx = null;
        if (e.key === 'ArrowRight') newIdx = (idx + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') newIdx = (idx - 1 + tabs.length) % tabs.length;
        if (newIdx === null) return;
        e.preventDefault();
        tabs[newIdx].focus();
        tabs[newIdx].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      });

      tab.addEventListener('focus', () => {
        tab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      });
    });
  });
}

function activateTab(tabBtn, section, tabSwipers, helpers) {
  const id = tabBtn.dataset.target;
  if (!id) {
    console.warn('tab-system: tab is missing data-target', tabBtn);
    return;
  }

  const swiperEl = section.querySelector('#' + CSS.escape(id));
  if (!swiperEl) {
    console.warn(`tab-system: no element matches #${id} in section`, section);
    return;
  }
  const wrapper = swiperEl.closest('.carousel-wrapper');

  // Update view-all link if this tab specifies a collection URL.
  const tabUrl = tabBtn.dataset.tabCollectionUrl;
  if (tabUrl) {
    section.querySelectorAll('.featured-collections__view-all').forEach((link) => {
      link.setAttribute('href', tabUrl);
    });
  }

  // Update tab UI states.
  section.querySelectorAll('.carousel-tab').forEach((btn) => {
    const isActive = btn === tabBtn;
    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  // Update wrapper visibility.
  if (wrapper) {
    section.querySelectorAll('.carousel-wrapper').forEach((panel) => {
      if (panel === wrapper) {
        panel.classList.remove('is-hidden');
        panel.removeAttribute('hidden');
        panel.setAttribute('aria-hidden', 'false');
      } else {
        panel.classList.add('is-hidden');
        panel.setAttribute('hidden', '');
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  }

  // Lazy-init the tab's swiper, or update it if already created.
  const existing = tabSwipers.get(id);
  if (existing && !existing.destroyed) {
    existing.update();
    return;
  }

  // Hydrate slides from the panel's <template> on first activation. Tabs 2-N
  // ship with their slides held inert in a template so the browser skips
  // style recalc / image fetch for them at page load. No-op on tab 1 (slides
  // are already inline) and on tabs the size-preference modal already
  // populated.
  hydrateTabFromTemplate(swiperEl);

  // The .swiper here is a direct descendant of the section (via the
  // .carousel-wrapper panel), so its data-attribute config comes from the
  // section element itself — same as the normal pipeline would treat it.
  const swiper = helpers.initSwiper(swiperEl, section);
  if (swiper) tabSwipers.set(id, swiper);
}

function hydrateTabFromTemplate(swiperEl) {
  const panel = swiperEl.closest('.carousel-wrapper');
  if (!panel) return;
  const template = panel.querySelector(':scope > template[data-tab-products-template]');
  if (!template) return;
  const wrapper = swiperEl.querySelector('.swiper-wrapper');
  if (!wrapper) return;
  if (wrapper.children.length === 0) {
    wrapper.appendChild(template.content.cloneNode(true));
  }
  template.remove();
}
